import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Headline, Text } from '@oneaudi/unified-web-alpha-components';
import useAnimations from '../../../hooks/useAnimations';
import FallbackCarDefault from '../../../assets/fallback_car_default.png';
import { device } from '../../../styles/devices';
import { StockCarTypes, Vehicle } from '../../../types';
import { SkeletonImage, SkeletonVehicle } from '../../Skeleton';
import ExposedHeader from '../ExposedHeader';
import {
  MobileWrapper,
  VehicleInfoContainer,
  ImageWrapper,
  VehicleImage,
  VehicleName,
  Trimline,
  Title,
} from '../VehicleInfo.styles';

interface VinSpecificVehicleProps {
  vehicle?: StockCarTypes;
  isVehicleLoading: boolean;
}

const VinSpecificVehicle: React.FC<VinSpecificVehicleProps> = ({ isVehicleLoading, vehicle }) => {
  const { t } = useTranslation();
  const isTablet = useMediaQuery({ query: device.tablet });
  const [isOpen, setIsOpen] = useState(false);

  const dropdownHeigh = 382;
  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(isOpen, dropdownHeigh);
  const image = vehicle?.images[0]?.url ?? FallbackCarDefault;

  const modelName = vehicle?.titleText;
  const trimName = vehicle?.subtitleText;

  useEffect(() => {
    if (isTablet) setIsOpen(false);
  }, [isTablet]);

  const renderVehicleInfo = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <>
          <VehicleName data-testid="vehicle-info-name">{modelName}</VehicleName>
          <Trimline>{trimName}</Trimline>
        </>
      );
    }

    return (
      <>
        <Headline
          data-testid="vehicle-info-name"
          margin={['8px', 0, 0, 0]}
          variant={{ type: 'headline', order: '3', weight: 'normal' }}
          tag="h2"
        >
          {modelName}
        </Headline>
        <Text
          margin={['12px', 0, 0, 0]}
          variant={{ type: 'copy', order: '1', style: 'normal' }}
          tag="p"
        >
          {trimName}
        </Text>
      </>
    );
  }, [modelName, trimName]);

  return (
    <>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <MobileWrapper style={props}>
              <VehicleInfoContainer style={filtersAccordianAnimation} data-testid="vehicle-info">
                <ImageWrapper data-testid="vehicle-info-image">
                  {isVehicleLoading ? (
                    <SkeletonImage data-testid="skeleton-image" />
                  ) : (
                    <VehicleImage src={image} />
                  )}
                </ImageWrapper>
                {isVehicleLoading ? (
                  <SkeletonVehicle data-testid="skeleton-vehicle" />
                ) : (
                  renderVehicleInfo
                )}
              </VehicleInfoContainer>
            </MobileWrapper>
          ),
      )}
      {!isTablet && (
        <ExposedHeader isOpened={isOpen} setIsOpened={setIsOpen} modelName={modelName} />
      )}
      {isTablet && (
        <VehicleInfoContainer data-testid="vehicle-info-tablet">
          {isVehicleLoading ? (
            <SkeletonImage data-testid="skeleton-image" />
          ) : (
            <ImageWrapper data-testid="vehicle-info-image">
              <VehicleImage src={image} />
            </ImageWrapper>
          )}
          {process.env.IS_LEGACY ? (
            <Title paddingTop>{t('digitalLeads.vehicle.vehicleOfInterest')}</Title>
          ) : (
            <Headline
              margin={['24px', 0, 0, 0]}
              variant={{ type: 'headline', order: '4', weight: 'normal' }}
              tag="h2"
            >
              {t('digitalLeads.vehicle.vehicleOfInterest')}
            </Headline>
          )}

          {isVehicleLoading ? (
            <SkeletonVehicle data-testid="skeleton-vehicle" />
          ) : (
            renderVehicleInfo
          )}
        </VehicleInfoContainer>
      )}
    </>
  );
};

export default VinSpecificVehicle;
